import { UsageType } from '../constants/usageType';
import { throwOnError } from './utils';

export type ProposedShop = {
  shop_name: string;
  shop_name_furigana: string;
  postal_code: string;
  address: string;
  tel: string;
  email: string;
  contact_person_name: string;
  contact_person_name_furigana: string;
  user_tag_group_item_ids?: string[];
  // 店舗形態（フロントで必須入力）。per-shop 属性。
  usage_type?: UsageType;
};

export type CreateOrganizationApplicationParams = {
  template_id: string;
  business_type: 'individual' | 'corporate';
  name: string;
  furigana: string;
  representative_name: string;
  representative_furigana: string;
  representative_birth_date: string; // YYYY-MM-DD
  postal_code: string;
  address: string;
  tel: string;
  email: string;
  bank_name: string;
  bank_code: string;
  bank_branch_name: string;
  bank_branch_code: string;
  bank_account_type: 'saving' | 'current';
  bank_account: string;
  bank_account_holder_name: string;
  private_money_ids: string[];
  head_office_shop: ProposedShop;
  // Conditional fields
  corporate_number?: string; // Required for corporate
  capital?: number;
  number_of_employees?: number;
  // 紹介元（個人・法人共通、business-type で gate しない）。
  referral_source?: string;
  additional_shops?: ProposedShop[];
};

export type OrganizationApplication = {
  id: string;
  template_id: string;
  status: 'submitted' | 'reviewing' | 'pending' | 'approved' | 'rejected';
  business_type: 'individual' | 'corporate';
  name: string;
  furigana: string;
  representative_name: string;
  representative_furigana: string;
  representative_birth_date: string;
  postal_code: string;
  address: string;
  tel: string;
  email: string;
  corporate_number: string | null;
  capital: number | null;
  number_of_employees: number | null;
  referral_source: string | null;
  bank_name: string;
  bank_code: string;
  bank_branch_name: string;
  bank_branch_code: string;
  bank_account_type: string;
  bank_account: string;
  bank_account_holder_name: string;
  private_money_ids: string[];
  created_at: string;
  updated_at: string;
};

export const createOrganizationApplication = (params: CreateOrganizationApplicationParams) =>
  fetch('/public/organization-applications', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: window._csrf_token, ...params }),
  }).then(throwOnError<OrganizationApplication>);

export type PublicUserTagGroupItem = {
  id: string;
  name_ja: string;
  name_en: string;
  name_zh_hans: string;
  name_zh_hant: string;
  name_ko: string;
  is_active: boolean;
  // 所属 subgroup の id。未所属・論理削除済み subgroup 所属は null（サーバが正規化済み）。
  user_tag_subgroup_id: string | null;
};

// 中間層 subgroup（整理・見出し用）。配下の active items をネストで持つ。
export type PublicUserTagSubgroup = {
  id: string;
  name_ja: string;
  name_en: string;
  name_zh_hans: string;
  name_zh_hant: string;
  name_ko: string;
  items: PublicUserTagGroupItem[];
};

export type PublicUserTagGroup = {
  id: string;
  name_ja: string;
  name_en: string;
  name_zh_hans: string;
  name_zh_hant: string;
  name_ko: string;
  max_selections: number;
  // active な subgroup（created_at 昇順）。無ければ null。見出し名・表示順に使う。
  subgroups: PublicUserTagSubgroup[] | null;
  // 全 active item（未分類含む）をフラットに保持。各 item に user_tag_subgroup_id。
  items: PublicUserTagGroupItem[];
};

type UserTagGroupOption = {
  user_tag_group_id: string;
  field_option: 'required' | 'optional';
  user_tag_group: PublicUserTagGroup;
};

// テンプレートに紐づくマネーの id と名称。申込完了画面で「何マネーへの加盟申込か」を
// 表示するためにサーバが private_money_ids から解決して返す（順序保持）。
export type PublicPrivateMoney = {
  id: string;
  name: string;
};

// Public template type (subset of admin template)
export type PublicOrganizationApplicationTemplate = {
  id: string;
  issuer_organization_code: string;
  title: string;
  status: 'draft' | 'active' | 'archived';
  application_term_url: string | null;
  // 申込完了後にリダイレクトする先のURL（任意）。未設定(null)ならリダイレクトしない。
  redirect_url: string | null;
  private_money_ids: string[];
  // private_money_ids に対応するマネーの id/名称（順序保持）。
  private_moneys: PublicPrivateMoney[];
  capital_field_option: 'required' | 'optional' | 'hidden';
  number_of_employees_field_option: 'required' | 'optional' | 'hidden';
  referral_source_field_option: 'required' | 'optional' | 'hidden';
  description: string;
  user_tag_group_options?: UserTagGroupOption[];
  created_at: string;
  updated_at: string;
};

export const getPublicOrganizationApplicationTemplate = (id: string) =>
  fetch(`/public/organization-application-templates/${id}`, {
    method: 'GET',
  }).then(throwOnError<PublicOrganizationApplicationTemplate>);

// Corporate info looked up from the National Tax Agency by corporate number.
export type PublicCorporateInfo = {
  name: string;
  furigana: string;
  postal_code: string;
  address: string;
};

export const getPublicCorporateInfo = (corporateNumber: string) =>
  fetch(`/public/corporate-numbers/${corporateNumber}`, {
    method: 'GET',
  }).then(throwOnError<PublicCorporateInfo>);
